<script setup lang="ts">
import { computed } from 'vue';
import Heart from './icons/Heart.vue';
import AlertCircle from './icons/AlertCircle.vue';
import HandHelping from './icons/HandHelping.vue';
import Lock from './icons/Lock.vue';
import MessageSquare from './icons/MessageSquare.vue';

const props = defineProps<{
  modelValue: boolean;
  commentsEnabled?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  hidePermanently: [];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => {
    emit('update:modelValue', value);
  },
});

const close = () => {
  isOpen.value = false;
};

const closeAndHide = () => {
  emit('hidePermanently');
  isOpen.value = false;
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <!-- Modal content with paper style - flex column to separate scroll area from buttons -->
      <div
        class="relative clipboard w-full max-w-2xl max-h-[90vh] flex flex-col bg-[#f5f5f0] rounded-lg shadow-2xl"
      >
        <!-- Paper texture -->
        <div
          class="absolute inset-0 opacity-20 pointer-events-none rounded-lg"
          style="
            background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E&quot;);
          "
        ></div>

        <!-- Header - fixed at top -->
        <div class="relative z-10 flex items-center justify-between p-6 pb-4">
          <h2 class="text-2xl font-bold text-gray-800">{{ $t('welcome.title') }}</h2>
          <button
            @click="close"
            class="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Content - scrollable area -->
        <div class="relative z-10 flex-1 overflow-y-auto px-6 text-base">
          <div class="space-y-6">
            <!-- Intro -->
            <p class="text-gray-700 leading-relaxed">{{ $t('welcome.intro') }}</p>

            <!-- Voting System Section -->
            <div>
              <h3 class="text-lg font-semibold text-gray-800 mb-3">
                {{ $t('welcome.votingTitle') }}
              </h3>

              <!-- Voting Note -->
              <p class="text-gray-600 leading-relaxed mb-4">
                {{ $t('welcome.votingNotePart1')
                }}<strong>{{ $t('welcome.votingNoteBold') }}</strong
                >{{ $t('welcome.votingNotePart2') }}
              </p>

              <div class="space-y-3">
                <!-- Liked -->
                <div class="flex items-start gap-3 bg-red-50 rounded-lg p-3">
                  <div
                    class="shrink-0 w-8 h-8 rounded-full bg-(--color-dot-liked) flex items-center justify-center"
                  >
                    <Heart :size="20" class="text-white" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-gray-800">{{ $t('welcome.likedTitle') }}</p>
                    <p class="text-gray-600">{{ $t('welcome.likedDesc') }}</p>
                    <div
                      class="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded text-sm text-gray-600"
                    >
                      <Lock :size="14" />
                      <span>{{ $t('welcome.privateVote') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Important -->
                <div class="flex items-start gap-3 bg-amber-50 rounded-lg p-3">
                  <div
                    class="shrink-0 w-8 h-8 rounded-full bg-(--color-dot-important) flex items-center justify-center"
                  >
                    <AlertCircle :size="20" class="text-white" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-gray-800">{{ $t('welcome.importantTitle') }}</p>
                    <p class="text-gray-600">{{ $t('welcome.importantDesc') }}</p>
                    <div
                      class="mt-2 flex items-start gap-1.5 px-2 py-1.5 bg-gray-100 rounded text-sm text-gray-600"
                    >
                      <Lock :size="14" class="shrink-0 mt-0.5" />
                      <span class="leading-relaxed">{{ $t('welcome.privateVote') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Supported -->
                <div class="flex items-start gap-3 bg-blue-50 rounded-lg p-3">
                  <div
                    class="shrink-0 w-8 h-8 rounded-full bg-(--color-dot-supported) flex items-center justify-center"
                  >
                    <HandHelping :size="20" class="text-white" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-gray-800">{{ $t('welcome.supportedTitle') }}</p>
                    <p class="text-gray-600">{{ $t('welcome.supportedDesc') }}</p>
                    <div
                      class="mt-2 flex items-start gap-1.5 px-2 py-1.5 bg-amber-100 rounded text-sm text-amber-800"
                    >
                      <Lock :size="14" class="shrink-0 mt-0.5" />
                      <span class="leading-relaxed"
                        >{{ $t('welcome.organizersCanSeePart1')
                        }}<strong>{{ $t('welcome.organizersCanSeeBold') }}</strong></span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Section - Only if comments enabled -->
            <div v-if="commentsEnabled" class="mt-6">
              <h3 class="text-lg font-semibold text-gray-800 mb-3">
                {{ $t('welcome.commentsTitle') }}
              </h3>

              <div class="flex items-start gap-3 bg-purple-50 rounded-lg p-3">
                <div
                  class="shrink-0 w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center"
                >
                  <MessageSquare class="w-4 h-4 text-white" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-gray-600">{{ $t('welcome.commentsDesc') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons - fixed at bottom -->
        <div class="relative z-10 flex gap-3 p-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            @click="close"
            class="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium cursor-pointer"
          >
            {{ $t('welcome.close') }}
          </button>
          <button
            type="button"
            @click="closeAndHide"
            class="push-button flex-1 px-4 py-3 rounded-lg font-medium"
          >
            {{ $t('welcome.closeAndHide') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
